import {
  CheckCircleFilled,
  FilterOutlined,
  PlusOutlined,
  WarningOutlined,
  UndoOutlined,
  TableOutlined,
  ApartmentOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { Tooltip, Button, Badge, Input, Select, Form } from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import GetFilteredTopPlansApi from "../../api/GetFilteredTopPlansApi";
import GetTopPlansApi from "../../api/GetTopPlansApi";
import GetHeadLevelPlanApi from "../../api/plans/GetHeadLevelPlanApi";
import DashboardBreadcrumb from "../../components/layout/DashboardBreadcrumb";
import AddTopPlanModal from "../../components/modal/add/AddTopPlanModal";
import TopPlanFilterModal from "../../components/modal/filters/TopPlanFilterModal";
import PageLoader from "../../components/sub/PageLoader";
import TopPlanTreeCards from "../../components/TopPlanTreeCards";
import PlansTree from "../../components/tree/PlansTree";
import { permissionAbility } from "../../helpers/permissionHelpers";
import { getPluralString } from "../../helpers/wordHelpers";
import { LIMIT_PER_PAGE } from "../../helpers/constants";

const { Option } = Select;

function TopPlans() {
  const [canStart, setCanStart] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const [table, setTable] = useState(false);
  const [treePlans, setTreePlans] = useState([]);
  const [data, setData] = useState([]);
  const [totalTopPlanCount, setTotalTopPlanCount] = useState(0);
  const [startAt, setStartAt] = useState(0);
  const [addModal, setAddModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [filterBy, setFilterBy] = useState();
  const [headLevel, setHeadLevel] = useState("");
  const [sort, setSort] = useState("ASC");
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const proHeight = document.body.clientHeight;
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;

  let curLocation = useLocation();
  const location = useLocation();

  let path = curLocation.search;
  let hasPlanGroup = path.split("/top-plans");
  const searchParams = new URLSearchParams(curLocation.search);
  let filterFromGraph = {
    planGroup: searchParams.get("planGroup"),
  };

  // 🔑 NEW: Track previous values for safety guards
  const [prevPerfCycleId, setPrevPerfCycleId] = useState(perfCycleState?.id);
  const [prevPositionId, setPrevPositionId] = useState(positionState?.id);

  // 🔑 NEW: Safety guard - Clear filters when performance cycle or position changes
  useEffect(() => {
    const perfCycleChanged = prevPerfCycleId && prevPerfCycleId !== perfCycleState?.id;
    const positionChanged = prevPositionId && prevPositionId !== positionState?.id;

    if (perfCycleChanged || positionChanged) {
      console.log("Clearing filters due to:", perfCycleChanged ? "perf cycle change" : "position change");
      
      // Clear filters
      sessionStorage.removeItem('topPlansFilters');
      setFilterStatus(false);
      setFilterBy(null);
      setData([]);
      setStartAt(0);
    }

    // Update previous values
    setPrevPerfCycleId(perfCycleState?.id);
    setPrevPositionId(positionState?.id);
  }, [perfCycleState?.id, positionState?.id]);

  // 🔑 NEW: Safety guard - Clear filters on logout
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Check if user is logging out (you might need to adjust this based on your logout mechanism)
      const isLoggingOut = !localStorage.getItem("authToken");
      if (isLoggingOut) {
        sessionStorage.removeItem('topPlansFilters');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    getHeadLevelPlan();
    
    // Check for saved filters in sessionStorage
    const savedFilters = sessionStorage.getItem('topPlansFilters');
    
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        console.log("Restoring filters:", parsedFilters);
        
        // Only restore if filters have actual values
        if (parsedFilters.title || parsedFilters.planGroup || parsedFilters.cascadeFrom) {
          setFilterBy(parsedFilters);
          setFilterStatus(true);
          getFilteredPlans(parsedFilters, 0, false, localStorage.getItem("sorting"));
          return; // Exit early to avoid running other logic
        }
      } catch (e) {
        console.error("Error parsing saved filters:", e);
      }
    }
    
    // Normal flow if no saved filters
    if (hasPlanGroup[0]?.slice(1, 10) === "planGroup") {
      getFilteredPlans(
        filterFromGraph,
        0,
        false,
        localStorage.getItem("sorting")
      );
    } else {
      getTopPlans(startAt, false, localStorage.getItem("sorting"));
    }
    
    setSort(localStorage.getItem("sorting"));
  }, [perfCycleState]);

  async function getTopPlans(num, newSet, sorting) {
    setTableLoading(true);
    const apiResponse = await GetTopPlansApi(
      perfCycleState.id,
      positionState.id,
      num,
      sorting,
      LIMIT_PER_PAGE
    );
    if (apiResponse.success) {
      if (apiResponse.data?.plans) {
        if (newSet) {
          setData(apiResponse.data?.plans);
        } else {
          setData([...data, ...apiResponse.data?.plans]);
        }
        setTable(true);
        if (apiResponse.data?.plans?.length !== 0) {
          setCanShow(true);
        }

        setTableLoading(false);
        setTotalTopPlanCount(apiResponse.data?.totalCount);
      }
      setCanStart(true);
    }
  }

  async function getFilteredPlans(values, num, newSet, sorting) {
    setTableLoading(true);
    const apiResponse = await GetFilteredTopPlansApi(
      positionState.id,
      perfCycleState.id,
      values.title || "",
      values.planGroup || "",
      values.cascadeFrom || "",
      num,
      LIMIT_PER_PAGE,
      sorting
    );
    if (apiResponse.success) {
      setFilterStatus(true);
      if (newSet) {
        setData(apiResponse.data?.plans);
      } else {
        setData([...data, ...apiResponse.data?.plans]);
      }
      setTotalTopPlanCount(apiResponse.data?.totalCount);
      setTableLoading(false);
      
      const filterData = {
        title: values.title,
        planGroup: values.planGroup,
        cascadeFrom: values.cascadeFrom,
      };
      
      setFilterBy(filterData);
      
      // Save filters to sessionStorage - simple and reliable
      sessionStorage.setItem('topPlansFilters', JSON.stringify(filterData));
      console.log("Saved filters to sessionStorage:", filterData);
      
      if (apiResponse.data?.plans?.length !== 0) {
        setTable(true);
        setCanShow(true);
      }

      setCanStart(true);
    }
  }

  function loadMore(num) {
    if (filterStatus) {
      getFilteredPlans(filterBy, num, false, localStorage.getItem("sorting"));
    } else {
      getTopPlans(num, false, localStorage.getItem("sorting"));
    }
  }

  function refreshData() {
    if (filterStatus) {
      getFilteredPlans(filterBy, 0, true, localStorage.getItem("sorting"));
    } else {
      getTopPlans(0, true, localStorage.getItem("sorting"));
    }
    setStartAt(0);
  }

  function changeSorting(type) {
    localStorage.setItem("sorting", type);
    setSort(type);
    if (filterStatus) {
      getFilteredPlans(filterBy, 0, true, localStorage.getItem("sorting"));
    } else {
      getTopPlans(0, true, type);
    }
  }

  function handleDelete(planId) {
    setTotalTopPlanCount(totalTopPlanCount - 1);
    const newData = [];
    data.forEach((element) => {
      if (planId !== element.planId) {
        newData.push(element);
      }
    });
    setData(newData);
    if (totalTopPlanCount - 1 === 0) {
      setCanShow(false);
    }
  }

  function handleEdit(editedPlan) {
    const newData = data.map((element) => {
      if (editedPlan.planId === element.planId) {
        return editedPlan;
      }
      return element;
    });
    setData(newData);
  }

  function resetFilter() {
    setData([]);
    setFilterStatus(false);

    // Clear saved filters from sessionStorage
    sessionStorage.removeItem('topPlansFilters');
    console.log("Cleared filters from sessionStorage");

    getTopPlans(0, true, localStorage.getItem("sorting"));
    setFilterBy();
  }

  async function getHeadLevelPlan() {
    const apiResponse = await GetHeadLevelPlanApi(
      perfCycleState.id,
      positionState.id
    );
    if (apiResponse.success) {
      setTreePlans(apiResponse.data);
      if (apiResponse.data?.length !== 0) {
        setCanShow(true);
      }

      setTableLoading(false);
    }
  }

  const right = table && (
    <>
      {sort === "DESC" ? (
        <Tooltip title={"Change order"}>
          <Button onClick={() => changeSorting("ASC")} className="mr-3">
            <ArrowDownOutlined />
          </Button>
        </Tooltip>
      ) : (
        <Tooltip title={"Change order"}>
          <Button onClick={() => changeSorting("DESC")} className="mr-3">
            <ArrowUpOutlined />
          </Button>
        </Tooltip>
      )}
      {canShow && (
        <>
          <Tooltip title={"Reset"}>
            <Button
              disabled={!filterStatus}
              onClick={() => {
                resetFilter();
              }}
              className="mr-3"
            >
              <UndoOutlined />
            </Button>
          </Tooltip>
          <Tooltip title={"Filter"}>
            <Badge
              count={
                filterStatus ? (
                  <CheckCircleFilled style={{ color: "#f5222d" }} />
                ) : (
                  0
                )
              }
            >
              <Button onClick={() => setFilterModal(true)}>
                <FilterOutlined />
              </Button>
            </Badge>
          </Tooltip>
        </>
      )}
      {permissionAbility(userLevel, "plans", "add") && (
        <Tooltip placement="left" title={`Add ${topPlanState}`}>
          <Button
            className="ml-3"
            onClick={() => {
              setAddModal(true);
            }}
            shape="circle"
            type="primary"
            icon={<PlusOutlined />}
          ></Button>
        </Tooltip>
      )}
    </>
  );

  const left = (
    <>
      <div className="pri-font text-lg">{getPluralString(topPlanState)}</div>
      {canShow &&
        (table ? (
          <Tooltip title={"Tree view"}>
            <Button onClick={() => setTable(false)} className="ml-3">
              <ApartmentOutlined />
            </Button>
          </Tooltip>
        ) : (
          <Tooltip title={"Table view"}>
            <Button onClick={() => setTable(true)} className="ml-3">
              <TableOutlined />
            </Button>
          </Tooltip>
        ))}
    </>
  );

  function showEmptyFilter() {
    return (
      <div className="flex justify-center items-center w-full h-full py-10">
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-lg pri-font text-gray-400">
            No {getPluralString(topPlanState)} available for the filter
          </div>
        </div>
      </div>
    );
  }

  function showEmptyCompanies() {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            No {getPluralString(topPlanState)} available
          </div>

          {permissionAbility(userLevel, "plans", "add") && (
            <>
              <div className="mb-3 text-gray-400">
                Add {getPluralString(topPlanState)} in your organization
              </div>
              <Button
                onClick={() => {
                  setAddModal(true);
                }}
                type="primary"
              >
                Add a {topPlanState}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  function renderTreeView() {
    return (
      <>
        {canShow ? (
          treePlans.length ? (
            <>
              <div className="mb-3">
                <Select
                  showSearch
                  placeholder="Select head level plan"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                  defaultValue={() => setHeadLevel(treePlans[0]?.id)}
                  onSelect={(value) => setHeadLevel(value)}
                  className={"w-full max-w-md"}
                >
                  {treePlans?.map((head) => {
                    return (
                      <Option key={head?.id} value={head?.id}>
                        {head.name}
                      </Option>
                    );
                  })}
                </Select>
              </div>
              <PlansTree plan={headLevel} />
            </>
          ) : (
            <div className="flex justify-center items-center w-full h-full">
              <div className="text-center">
                <div className="text-3xl text-gray-400">
                  <WarningOutlined />
                </div>
                <div className="text-xl pri-font text-gray-400">
                  No hierarchy available
                </div>
              </div>
            </div>
          )
        ) : (
          <div
            style={{
              height: proHeight - 188,
            }}
          >
            <PageLoader />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <Content style={{ backgroundColor: "#fff" }}>
        <DashboardBreadcrumb left={left} right={right} />
        <div
          style={{
            height: proHeight - 122,
            overflow: "auto",
          }}
          className="custom-scroll p-6"
        >
          {canStart ? (
            canShow ? (
              table ? (
                <div className="mb-5">
                  <TopPlanTreeCards
                    data={data}
                    setStartAt={(num) => setStartAt(num)}
                    loadMore={(num) => loadMore(num)}
                    loading={tableLoading}
                    startAt={startAt}
                    totalTopPlanCount={totalTopPlanCount}
                    handleDelete={(planId) => handleDelete(planId)}
                    handleEdit={(editedPlan) => handleEdit(editedPlan)}
                    onDataUpdate={refreshData}
                  />
                  {data?.length === 0 && !tableLoading && showEmptyFilter()}
                </div>
              ) : (
                renderTreeView()
              )
            ) : (
              showEmptyCompanies()
            )
          ) : (
            <PageLoader />
          )}
        </div>
      </Content>
      <AddTopPlanModal
        visible={addModal}
        handleCancel={() => {
          setAddModal(false);
        }}
        handleOk={(newPlan) => {
          // 🔑 NEW: Add new plan at the TOP of the list
          const newData = [newPlan, ...data];
          setData(newData);
          setStartAt(startAt + 1);

          totalTopPlanCount === 0 && setCanShow(true);
          setTotalTopPlanCount(totalTopPlanCount + 1);
        }}
      />
      <TopPlanFilterModal
        visible={filterModal}
        handleCancel={() => setFilterModal(false)}
        handleOk={(values) => {
          // Check if all filter values are empty (clear action)
          const isEmpty = !values.title && !values.planGroup && !values.cascadeFrom;
          
          if (isEmpty) {
            // Clear filters and reload normal data
            console.log("Clearing filters");
            setFilterStatus(false);
            setFilterBy(null);
            sessionStorage.removeItem('topPlansFilters');
            getTopPlans(0, true, localStorage.getItem("sorting"));
          } else {
            // Apply filters normally
            getFilteredPlans(values, 0, true, localStorage.getItem("sorting"));
          }
        }}
        filterBy={filterBy}
      />
    </>
  );
}

export default TopPlans;
